import { Anchor, Icon } from '@ampol/design-system';
import React, { useState } from 'react';
import './header.scss';
import 'hamburgers/_sass/hamburgers/hamburgers.scss';
import { useSelector, useDispatch } from 'react-redux';
import { DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown } from 'reactstrap';
import { logout } from '../../actions/userActions';
import { fetchNotifications } from 'actions/notificationActions';

import NavMenu from '../nav-menu/nav-menu';
import HeaderNotificationMenuWrapper from '../header-notification-menu-wrapper/header-notification-menu-wrapper';
import MobileNavMenu from '../mobile-nav-menu/mobile-nav-menu';
import Env from '../../Env';
import OutsideClickHandler from 'react-outside-click-handler';
import UserSearch from '../user-search/user-search';
import HeaderAccountSelector from './header-account-selector';
import Links from 'constants/links';
import { useNotification } from 'components/use-notification/use-notification';
import { useTrackPageViews } from 'components/use-track-page-views/use-track-page-view';
import { trackCustomEvent } from 'utilities/app-insights';
import GlobalSearch from 'components/global-search/GlobalSearch';

const Header = ({ routes, onYourProfileClick, maintenanceActive }) => {
  useNotification();
  useTrackPageViews();

  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const canImpersonateUser = useSelector((state) => state.userStore.canImpersonateUser);
  const notificationsList = useSelector((state) => state.notifications.notificationsList);

  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [hasSearchActive, setHasSearchActive] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  const isAdminPortal = Env.isAdminPortal();

  const toggleNotificationMenu = () => {
    if (!open) {
      window.dataLayer.push({
        event: 'Primary_Nav_Notifications_Bell_Click',
        open: true,
      });
      trackCustomEvent(
        'Click Notifications Bell',
        loggedInUser,
        undefined,
        'Clicked on bell icon to view notifications.'
      );
      dispatch(fetchNotifications());
    }

    const isMobile = window.innerWidth < 768;
    const overflowValue = isMobile && !open ? 'hidden' : 'auto';

    document.body.style.overflow = overflowValue;
    document.getElementById('bodyScroll').style.overflow = overflowValue;

    setOpen(!open);
  };

  const handleOutsideClick = () => {
    if (open && !isNotificationModalOpen) {
      toggleNotificationMenu();
    }
  };

  const onLogoutClick = () => {
    window.dataLayer.push({
      event: 'Secondary_Nav_Logout_Click',
      click: true,
    });
    trackCustomEvent('Logout', loggedInUser, undefined, 'User logged out from the application.');
    dispatch(logout());
  };

  const notificationIconState = () => {
    const hasUnRead = notificationsList.some((x) => !x.isRead);

    return hasUnRead ? 'bottom-nav__notification-icon--unread' : '';
  };

  const resetPassword = () => {
    //Initiate reset password flow from the url redirection.
    //The approach is workaround to allow user to reset password and on the success or cancellation shold be able to
    //continue using portal without having to SignIn.
    //The approach is a workaround where the existing valid session is renewed and continued after the successful reset password User is redirected back to the portal
    window.location.href = Env.getResetPasswordUrl();
  };

  const handleContactUsClick = () => {
    window.dataLayer.push({
      event: 'Secondary_Nav_Contact_Us_Click',
      click: true,
    });
    trackCustomEvent('Contact Us', loggedInUser, undefined, 'Clicked on Contact Us link to open Contact Us page.');
  };

  const handleSupportClick = () => {
    window.dataLayer.push({
      event: 'Secondary_Nav_Support_Click',
      click: true,
    });
    trackCustomEvent('Support', loggedInUser, undefined, 'Clicked on Support link to open FAQ page.');
  };

  return (
    <header className='header'>
      <nav
        className={`top-nav ${canImpersonateUser || (loggedInUser.userImpersonationInfo && !isAdminPortal) ? 'top-nav--visible' : ''}`}
      >
        {canImpersonateUser && <UserSearch onSearchActive={(isActive) => setHasSearchActive(isActive)} />}
        {loggedInUser.userImpersonationInfo && !isAdminPortal && (
          <ImpersonationInfo userImpersonationInfo={loggedInUser.userImpersonationInfo} />
        )}
        {!isAdminPortal && <GlobalSearch />}
        <Anchor href={Links.CONTACT_US} className='top-nav__item' onClick={handleContactUsClick}>
          <span className='top-nav__item-label'>Contact Us</span>
          <Icon url={'/icons/chat.svg'} />
        </Anchor>
        <Anchor href={Links.SUPPORT} className='top-nav__item' onClick={handleSupportClick}>
          <span className='top-nav__item-label'>Support</span>
          <Icon url={'/icons/support.svg'} />
        </Anchor>
        <AccountDropdown
          onYourProfileClick={onYourProfileClick}
          logoutUser={onLogoutClick}
          resetPassword={resetPassword}
          user={loggedInUser}
        />
      </nav>
      <nav className='bottom-nav'>
        <a href='/' className='bottom-nav__ampol-logo'>
          <img src={'/logo-secondary.svg'} alt='Ampol' />
        </a>
        {!isAdminPortal && (
          <div className='bottom-nav__header-account-selector'>
            <HeaderAccountSelector />
          </div>
        )}
        <div className='bottom-nav__menu-items'>
          <NavMenu resetPassword={resetPassword} routes={routes} />
        </div>
        <div className='bottom-nav__notification'>
          <OutsideClickHandler onOutsideClick={handleOutsideClick}>
            <button
              onClick={toggleNotificationMenu}
              className={`bottom-nav__notification-icon ${notificationIconState()}`}
            >
              <Icon url={'/icons/bell.svg'} />
            </button>
            {open && (
              <div>
                <HeaderNotificationMenuWrapper
                  hasTopBar={canImpersonateUser}
                  isSearchActive={hasSearchActive}
                  isImpersonating={!!loggedInUser.userImpersonationInfo}
                  isMaintenanceActive={maintenanceActive}
                  setIsNotificationModalOpen={setIsNotificationModalOpen}
                />
              </div>
            )}
          </OutsideClickHandler>
        </div>
        <div className='header__mobile-menu'>
          <MobileNavMenu
            logoutUser={onLogoutClick}
            resetPassword={resetPassword}
            onYourProfileClick={onYourProfileClick}
            routes={routes}
            hasTopBar={canImpersonateUser}
            isSearchActive={hasSearchActive}
            isImpersonating={!!loggedInUser.userImpersonationInfo}
            isMaintenanceActive={maintenanceActive}
          />
        </div>
      </nav>
      {!isAdminPortal && (
        <div className='header__mobile'>
          <HeaderAccountSelector />
        </div>
      )}
    </header>
  );
};

const ImpersonationInfo = ({ userImpersonationInfo }) => {
  const { givenName, familyName, emailId } = userImpersonationInfo;

  return (
    <div className='top-nav__item top-nav__impersonation-info'>
      Impersonating:
      <div className='top-nav__impersonation-details'>
        <div className='top-nav__impersonation-name'>
          {givenName} {familyName}
        </div>
        <div className='top-nav__impersonation-email'>{emailId}</div>
      </div>
    </div>
  );
};

const AccountDropdown = ({ onYourProfileClick, logoutUser, user }) => (
  <UncontrolledDropdown className='top-nav__item'>
    <DropdownToggle className='top-nav__dropdown-toggle' variant='success'>
      <span className='top-nav__dropdown-toggle-name'>
        {user.givenName} {user.familyName}
      </span>
      <Icon url='/icons/user.svg' />
      <Icon url='/icons/chevron-down.svg' />
    </DropdownToggle>
    <DropdownMenu right className='top-nav__dropdown-menu'>
      <DropdownItem className='top-nav__dropdown-item' onClick={onYourProfileClick}>
        Your Profile
      </DropdownItem>
      {/* TASK-33286 Remove Reset Password Link */}
      {/* <DropdownItem className="menu__dropdown-item" onClick={resetPassword}>Reset Password</DropdownItem> */}
      <DropdownItem className='top-nav__dropdown-item' onClick={logoutUser}>
        Logout
      </DropdownItem>
    </DropdownMenu>
  </UncontrolledDropdown>
);

export default Header;
